<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container">
      <div class="modal-header">
        <h6>{{ tipo === 'OFX' ? 'Importar OFX' : 'Importar arquivo da operadora' }}</h6>
        <i class="bi bi-x-circle close-icon" @click="$emit('close')"></i>
      </div>

      <div class="spacer"></div>

      <div class="modal-content">
        <div class="form-group mb-3">
          <label>Conta financeira:</label>
          <select
            v-model="form.conta_financeira_id"
            class="form-control"
            :disabled="loading || Boolean(vinculoPendente)"
          >
            <option value="">Selecione</option>
            <option v-for="conta in contas" :key="conta.id" :value="String(conta.id)">
              {{ conta.apelido }} - {{ conta.agencia }} - {{ conta.numero_conta }}
            </option>
          </select>
        </div>

        <div v-if="tipo === 'OPERADORA'" class="form-group mb-3">
          <label>Operadora:</label>
          <input
            v-model="form.operadora_nome"
            class="form-control"
            placeholder="Ex.: Getnet"
            :disabled="loading || Boolean(vinculoPendente)"
          />
        </div>

        <div v-if="tipo === 'OPERADORA'" class="form-group mb-3">
          <label>
            <input v-model="form.somente_banco_operadora" type="checkbox" :disabled="loading || Boolean(vinculoPendente)" />
            Reprocessar somente Banco x Operadora
          </label>
          <small class="d-block text-muted">
            Use o mesmo CSV já importado para validar os repasses e vinculá-los ao extrato bancário.
            Receitas, despesas e taxas permanecem preservadas.
          </small>
        </div>

        <div class="form-group mb-3">
          <label>Arquivo:</label>
          <input
            ref="fileInput"
            type="file"
            class="form-control"
            :accept="accept"
            :disabled="loading || Boolean(vinculoPendente)"
            @change="onFileChange"
          />
        </div>

        <div
          v-if="vinculoPendente"
          class="alert alert-warning mb-3"
        >
          <div class="fw-bold mb-2">
            Primeira utilização desta identidade
          </div>

          <div class="mb-2">
            <strong>Conta selecionada:</strong><br />
            {{ contaSelecionada?.apelido || '—' }}
            <span v-if="contaSelecionada">
              · Ag. {{ contaSelecionada.agencia }}
              · Conta {{ contaSelecionada.numero_conta }}
            </span>
          </div>

          <template
            v-if="vinculoPendente?.detalhes?.tipo === 'OFX'"
          >
            <div>
              <strong>Identidade detectada no OFX:</strong>
            </div>
            <div>
              Banco:
              {{ vinculoPendente.detalhes.identidade?.bankid || '—' }}
            </div>
            <div>
              ACCTID:
              {{ vinculoPendente.detalhes.identidade?.acctid || '—' }}
            </div>
            <div v-if="vinculoPendente.detalhes.identidade?.accttype">
              Tipo:
              {{ vinculoPendente.detalhes.identidade.accttype }}
            </div>
          </template>

          <template v-else>
            <div>
              <strong>Identidade detectada na operadora:</strong>
            </div>
            <div>
              Operadora:
              {{
                vinculoPendente.detalhes.identidade
                  ?.operadora_nome || '—'
              }}
            </div>
            <div>
              EC:
              {{
                vinculoPendente.detalhes.identidade
                  ?.ec_centralizador || '—'
              }}
            </div>
            <div>
              CNPJ/CPF:
              {{
                formatarDocumento(
                  vinculoPendente.detalhes.identidade?.documento
                )
              }}
            </div>
          </template>

          <hr class="my-2" />

          <small>
            Ao confirmar, esta identidade ficará vinculada à conta
            financeira selecionada e será utilizada para validar as
            próximas importações.
          </small>
        </div>

        <div v-if="erro" class="alert alert-danger py-2 mb-3">
          {{ erro }}
        </div>

        <div class="d-flex justify-content-end mt-4 mb-2">
          <button
            class="btn btn-secondary me-2"
            @click="$emit('close')"
            :disabled="loading"
          >
            Cancelar
          </button>

          <button
            v-if="vinculoPendente"
            class="btn btn-success"
            @click="confirmarVinculoEImportar"
            :disabled="loading"
          >
            {{
              loading
                ? 'Vinculando...'
                : 'Vincular e importar'
            }}
          </button>

          <button
            v-else
            class="btn btn-primary"
            @click="submit"
            :disabled="loading"
          >
            {{ loading ? 'Enviando...' : 'Importar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import {
  importarOfx,
  importarOperadora,
  vincularIdentidadeOfx,
  vincularIdentidadeOperadora
} from '@/services/conciliacaoBancaria.api'

const props = defineProps({
  tipo: { type: String, required: true },
  contas: { type: Array, default: () => [] },
  dataInicio: { type: String, default: '' },
  dataFim: { type: String, default: '' }
})

const emit = defineEmits(['close', 'importado'])

const loading = ref(false)
const erro = ref('')
const file = ref(null)
const fileInput = ref(null)
const vinculoPendente = ref(null)

const form = reactive({
  conta_financeira_id: '',
  operadora_nome: 'GETNET',
  somente_banco_operadora: false
})

const accept = computed(() =>
  props.tipo === 'OFX'
    ? '.ofx'
    : '.csv,.txt'
)

const contaSelecionada = computed(() =>
  (props.contas || []).find(
    conta =>
      String(conta.id) ===
      String(form.conta_financeira_id)
  ) || null
)

function formatarDocumento(value) {
  const digitos = String(value || '').replace(/\D/g, '')

  if (digitos.length === 14) {
    return digitos.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      '$1.$2.$3/$4-$5'
    )
  }

  if (digitos.length === 11) {
    return digitos.replace(
      /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
      '$1.$2.$3-$4'
    )
  }

  return digitos || '—'
}

function onFileChange(event) {
  file.value = event.target.files?.[0] || null
  vinculoPendente.value = null
  erro.value = ''
}

function validarFormulario() {
  if (!form.conta_financeira_id) {
    erro.value = 'Selecione a conta financeira.'
    return false
  }

  if (
    props.tipo === 'OPERADORA' &&
    !form.operadora_nome
  ) {
    erro.value = 'Informe a operadora.'
    return false
  }

  if (!file.value) {
    erro.value = 'Selecione um arquivo.'
    return false
  }

  return true
}

function criarFormData({ incluirPeriodo = false } = {}) {
  const fd = new FormData()

  fd.append('arquivo', file.value)
  fd.append(
    'conta_financeira_id',
    form.conta_financeira_id
  )

  if (props.tipo === 'OPERADORA') {
    fd.append('somente_banco_operadora', String(form.somente_banco_operadora))
    fd.append(
      'operadora_nome',
      form.operadora_nome
    )
  }

  if (incluirPeriodo && props.dataInicio) {
    fd.append('data_inicio', props.dataInicio)
  }

  if (incluirPeriodo && props.dataFim) {
    fd.append('data_fim', props.dataFim)
  }

  return fd
}

async function executarImportacao() {
  const fd = criarFormData({
    incluirPeriodo: true
  })

  return props.tipo === 'OFX'
    ? importarOfx(fd)
    : importarOperadora(fd)
}

function tratarErroImportacao(e) {
  const data = e?.response?.data

  if (
    data?.code === 'IDENTIDADE_NAO_VINCULADA' &&
    data?.pode_vincular === true
  ) {
    vinculoPendente.value = {
      code: data.code,
      error: data.error,
      detalhes: data.detalhes || {}
    }

    erro.value = ''
    return true
  }

  vinculoPendente.value = null

  erro.value =
    data?.error ||
    data?.message ||
    (typeof data === 'string' ? data : '') ||
    'Erro ao importar arquivo.'

  return false
}

async function submit() {
  erro.value = ''
  vinculoPendente.value = null

  if (!validarFormulario()) {
    return
  }

  loading.value = true

  try {
    const response = await executarImportacao()

    emit('importado', response.data)
    emit('close')
  } catch (e) {
    tratarErroImportacao(e)
  } finally {
    loading.value = false
  }
}

async function confirmarVinculoEImportar() {
  erro.value = ''

  if (
    !vinculoPendente.value ||
    !validarFormulario()
  ) {
    return
  }

  loading.value = true

  try {
    /*
     * O frontend não envia BANKID, ACCTID, EC ou documento.
     *
     * O mesmo arquivo é enviado novamente e o backend relê
     * sua identidade antes de criar o vínculo.
     */
    const fdVinculo = criarFormData()

    if (props.tipo === 'OFX') {
      await vincularIdentidadeOfx(fdVinculo)
    } else {
      await vincularIdentidadeOperadora(fdVinculo)
    }

    /*
     * Somente depois do vínculo confirmado fazemos uma nova
     * chamada ao endpoint normal de importação.
     */
    const response = await executarImportacao()

    vinculoPendente.value = null

    emit('importado', response.data)
    emit('close')
  } catch (e) {
    const data = e?.response?.data

    /*
     * Se houve conflito durante a confirmação, não deixamos
     * uma confirmação antiga disponível para novo clique.
     */
    if (
      data?.code ===
        'IDENTIDADE_VINCULADA_OUTRA_CONTA' ||
      data?.code ===
        'IDENTIDADE_VINCULADA_CONCORRENTE' ||
      data?.code ===
        'IDENTIDADE_OFX_NAO_CORRESPONDE_CONTA'
    ) {
      vinculoPendente.value = null
    }

    erro.value =
      data?.error ||
      data?.message ||
      (typeof data === 'string' ? data : '') ||
      'Erro ao vincular a identidade.'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.modal-header {
  background: #ccc;
  padding: 10px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-container {
  background: #fff;
  width: 100%;
  max-width: 520px;
  overflow-y: auto;
  margin: 0 10px;
  border-radius: 0;
}

.modal-content {
  padding: 15px;
}

.close-icon {
  font-size: 1.5rem;
  cursor: pointer;
}

.spacer {
  margin-bottom: 20px;
}

.form-group label {
  font-weight: bold;
  margin-bottom: 5px;
  display: block;
}

.form-control {
  width: 100%;
  padding: 5px;
  border: 1px solid #ced4da;
  border-radius: 0;
}
</style>